import { Component, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, errorId: null };

  static getDerivedStateFromError(): State {
    return { hasError: true, errorId: crypto.randomUUID() };
  }

  override componentDidCatch() {
    // Log error ID only — never log component props or state that may contain PHI.
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-lg border border-red-100 bg-red-50 p-8 text-center">
          <AlertTriangle className="h-8 w-8 text-red-400" />
          <p className="text-sm font-medium text-red-800">
            Something went wrong displaying this section.
          </p>
          <p className="text-xs text-red-600">
            Refresh the page to try again. If the problem persists, contact support.
          </p>
          {this.state.errorId && (
            <p className="text-[11px] text-red-400">Ref: {this.state.errorId}</p>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
